/**
 * Secure Layout
 *
 * Handles authentication, NPI verification, and account details confirmation for secure routes.
 *
 * Responsibilities:
 * - Redirects unauthenticated users to public entry immediately
 * - Redirects users needing NPI verification to verification page, except for allowed routes
 * - Redirects users needing account details confirmation to confirmation page
 * - Allows users needing NPI verification to access directory, community, and inquiry routes
 * - Renders nothing while redirecting or loading to prevent flash of secure content
 * - Provides base stack navigation for authenticated routes
 * - Only runs redirects when inside the secure group
 * - Account linking context guard prevents redirect flicker when linking flow is in progress
 * - Declares every screen in the group, and drops the ones a reader may not reach
 *
 * Those last two are different mechanisms and the difference is the point. A redirect moves
 * a reader who is on a route; a guard means there is no route to be on, so nothing has to
 * detect them or chase them off one. Where a rule is pure reachability it is written as a
 * guard here, which is what let `fellows/index` and `primum-settings` stop redirecting from
 * inside their own render. Where a rule has to compute a *destination* — carrying
 * `redirect_path` through verification, resuming a link after sign-in — it stays a redirect,
 * because a guard has no destination to give: it empties the navigator and the router falls
 * back to `routeNames[0]`.
 */
import { isWeb } from '@gluestack-ui/nativewind-utils/IsWeb';
import { useQueryClient } from '@tanstack/react-query';
import { Stack, usePathname } from 'expo-router';
import { useEffect, useMemo, useState } from 'react';

import { ActingAsBanner } from '@/components/ActingAsBanner';
import { AppAbilityProvider } from '@/components/AppAbilityProvider';
import { CurrentUserUnreachable } from '@/components/CurrentUserUnreachable';
import { GateRedirect } from '@/components/GateRedirect';
import { GlobalBannerSlot } from '@/components/GlobalBannerSlot';
import { NavBar } from '@/components/navBar';
import {
  hidesSecureNavBar,
  isPublicBrowseRoute,
  isPubliclyBrowsableRoute,
  isPublicProfileView,
  isPublicTabRoot,
  ROUTES,
} from '@/constants/routes';
import { BASE_STACK_SCREEN_OPTIONS } from '@/constants/screenOptions';
import { FIFTEEN_MINUTES, TEN_SECONDS } from '@/constants/time';
import { useInquiryCloseNavigationEffect } from '@/hooks/inbox/useInquiryCloseNavigationEffect';
import { useAuthStatus } from '@/hooks/useAuthStatus';
import { useColors } from '@/hooks/useColors';
import {
  buildAuthPathWithRedirect,
  useEntryDestination,
  useRedirectPath,
} from '@/hooks/useRedirectPath';
import { useIsCompactLayout } from '@/hooks/useResponsiveLayout';
import { useSecureRouteAccess } from '@/hooks/useSecureRouteAccess';
import { useShareLinkQuickActionRouting } from '@/hooks/useShareLinkQuickAction';
import { useStalled } from '@/hooks/useStalled';
import { getCurrentUserV2QueryOptions } from '@/services/getCurrentUserV2';
import {
  clearAccountLinkingContext,
  hasNoAccountLinkingContext,
  loadAccountLinkingContext,
} from '@/services/accountLinking';
import { sanitizeRedirectDestination } from '@/util/redirectDestination';
import { isEHRSession } from '@/util/smartLaunch';

/**
 * The login route named inside its own group.
 *
 * A bare `/login` resolves relative to the group the router is currently in, so the gate
 * would send a visitor to a login route nested under the secure group — which the gate
 * then bounces again, losing the destination on the second pass.
 */
const OPEN_LOGIN_PATHNAME = '/(open)/login';

/**
 * Where a signed-out visitor goes, and what they take with them.
 *
 * A link that names an authenticated resource is the whole reason someone opened the app,
 * so the destination rides along as `redirect_path` and every auth screen downstream
 * already knows to hand it back afterwards. Dropping it here is what stranded people on
 * the directory with no way to recover where the link pointed.
 *
 * Which surface they land on depends on whether the destination can be shown at all
 * without an account. A route with a public twin is browsable as-is, so sending someone to
 * a sign-in wall for it would be an interruption with nothing behind it. Everything else
 * renders nothing until they authenticate, and asking for a sign-in is the only thing that
 * can actually get them there — landing them on the public directory instead reads as the
 * link having been wrong.
 *
 * Either way the destination is carried, so signing in later from the nav bar resumes it
 * just as signing in from the prompt does. The one exception is a destination that names
 * the landing itself, which would only add a parameter describing where the visitor
 * already is.
 */
function buildUnauthenticatedBounce(destination: string | null): string {
  const publicLandingPathname = isWeb ? '/(open)/directory' : '/';

  const landsWhereItAlreadyIs = isWeb && destination === ROUTES.DIRECTORY;

  if (!destination || landsWhereItAlreadyIs) {
    return publicLandingPathname;
  }

  return buildAuthPathWithRedirect(
    isPubliclyBrowsableRoute(destination) ? publicLandingPathname : OPEN_LOGIN_PATHNAME,
    destination
  );
}

/** Secure routes a signed-in user is parked on until they finish something. */
const NPI_VERIFICATION_PATHNAME = '/(secure)/npi-verification';

const CONFIRM_DETAILS_PATHNAME = '/(secure)/confirm-account-details';

/**
 * A route on `SECURE_ROUTES_WITHOUT_NAV_BAR` only needs the bar out of the way on a
 * compact width, where it would take height from a full-screen workspace. Desktop web
 * has room for both, and hiding the bar there leaves no way back to the rest of the app.
 *
 * The compact check is its own component, mounted only for a route on that list, so
 * every other secure screen renders `NavBar` without subscribing to breakpoint changes
 * at all — resizing anywhere else doesn't re-render `SecureLayout`'s whole subtree.
 */
function SecureNavBar({ pathname }: { pathname: string }) {
  if (!hidesSecureNavBar(pathname)) {
    return <NavBar />;
  }

  return <CompactAwareNavBar />;
}

function CompactAwareNavBar() {
  const isCompactLayout = useIsCompactLayout();

  return isCompactLayout ? null : <NavBar />;
}

export default function SecureLayout() {
  const { colors, statusBarStyle } = useColors();

  const pathname = usePathname();

  const entryDestination = useEntryDestination();

  const { redirectPath } = useRedirectPath();

  /*
   * An explicit `redirect_path` already on the route wins over the path itself: it is set
   * by the flows that detour a user through verification, and it names where they were
   * originally headed rather than the screen the detour parked them on.
   */
  const capturedDestination = useMemo(
    () => sanitizeRedirectDestination(redirectPath ?? entryDestination),
    [redirectPath, entryDestination]
  );

  const {
    userId,
    currentUser,
    isUnauthenticated,
    needsNpiVerification,
    needsAccountDetailsConfirmation,
    isLoading,
    isAuthResolved,
  } = useAuthStatus();

  const queryClient = useQueryClient();

  /**
   * The account query retries, fails and starts over while the API is unreachable, so
   * `isLoading` flips on and off and never ends in an answer, and the guard below would
   * hold a blank screen for good. The verdict holds until the account arrives.
   */
  const accountUnreachable = useStalled(isLoading, TEN_SECONDS, currentUser !== undefined);

  useInquiryCloseNavigationEffect();

  useShareLinkQuickActionRouting();

  /*
   * Called unconditionally, above the redirects below, because a hook cannot be skipped —
   * and read only in the guards at the bottom of this file. Its queries are cached and
   * shared, so a render that never reaches those guards costs a cache read.
   */
  const { mayUseVerifiedRoutes, mayUsePrimumSettings } = useSecureRouteAccess();

  /*
   * Seeded synchronously where storage allows it, so the overwhelmingly common case — no
   * linking flow in progress — is decided on the first render rather than a tick later.
   * Waiting is what lets the router tear the navigator down and rewrite the URL beneath a
   * guard that is still making up its mind.
   */
  const [linkingCtxStatus, setLinkingCtxStatus] = useState<'unknown' | 'present' | 'absent'>(() =>
    hasNoAccountLinkingContext() ? 'absent' : 'unknown'
  );

  useEffect(() => {
    let isMounted = true;

    const checkLinkingContext = async () => {
      try {
        const ctx = await loadAccountLinkingContext();

        if (ctx && ctx.createdAt) {
          const now = Date.now();

          if (now - ctx.createdAt > FIFTEEN_MINUTES) {
            await clearAccountLinkingContext();

            if (isMounted) {
              setLinkingCtxStatus('absent');
            }

            return;
          }

          if (isMounted) {
            setLinkingCtxStatus('present');
          }

          return;
        }

        if (isMounted) {
          setLinkingCtxStatus('absent');
        }
      } catch {
        if (isMounted) {
          setLinkingCtxStatus('absent');
        }
      }
    };

    checkLinkingContext();

    return () => {
      isMounted = false;
    };
  }, []);

  /*
   * Read off `capturedDestination`, not the live `pathname`, deliberately. Rendering the
   * redirect below to a *different* top-level group (`(open)`) tears this layout's own
   * stack down mid-transition, and on the render that catches it `usePathname()` has
   * already collapsed to `/` — a real, observed value, not a hypothetical one. Deciding
   * from that would flip this check to false on the very render meant to act on it and
   * send a valid profile view to the signed-out bounce instead. `capturedDestination`
   * survives that collapse for the same reason `buildUnauthenticatedBounce` below already
   * relies on it doing so.
   */
  const isPublicProfileViewPath = isPublicProfileView(capturedDestination ?? '');

  /*
   * Every decision this layout makes is a redirect it renders, and each is made exactly
   * once. They used to be made twice — an effect that navigated and a render body that
   * returned `null` for the same conditions — which is two copies of one rule that have to
   * be kept in agreement, and they had already drifted apart on native. Rendering the
   * redirect is also what keeps the router from tearing down the stack underneath a layout
   * that is still deciding, which is what rewrote the address bar to an unrelated screen.
   */
  if (!isAuthResolved) {
    return null;
  }

  if (isUnauthenticated) {
    if (isWeb && (isPublicProfileViewPath || isPublicTabRoot(capturedDestination ?? ''))) {
      return <GateRedirect href={`/(open)${capturedDestination}`} />;
    }

    if (linkingCtxStatus !== 'absent') {
      return null;
    }

    if (isEHRSession()) {
      return <GateRedirect href={ROUTES.SMART_ENDED} />;
    }

    return <GateRedirect href={buildUnauthenticatedBounce(capturedDestination)} />;
  }

  if (accountUnreachable) {
    return (
      <CurrentUserUnreachable
        onRetry={() =>
          void queryClient.refetchQueries({
            queryKey: getCurrentUserV2QueryOptions(userId).queryKey,
          })
        }
      />
    );
  }

  if (isLoading) {
    return null;
  }

  const onNpiVerificationPage = pathname.includes('/npi-verification');

  /*
   * Held to the same list as the signed-out bounce: a route a visitor can browse without an
   * account is one a reader awaiting verification can browse too. Profile is deliberately
   * not folded in — the wider `isPubliclyBrowsableRoute` would also admit the profile
   * editor, which verification is meant to gate.
   */
  const mayBrowseWhilePendingNpi = isWeb && isPublicBrowseRoute(pathname);

  if (needsNpiVerification && !onNpiVerificationPage && !mayBrowseWhilePendingNpi) {
    return (
      <GateRedirect
        href={buildAuthPathWithRedirect(NPI_VERIFICATION_PATHNAME, capturedDestination)}
      />
    );
  }

  if (needsAccountDetailsConfirmation && !pathname.includes('/confirm-account-details')) {
    return (
      <GateRedirect
        href={buildAuthPathWithRedirect(CONFIRM_DETAILS_PATHNAME, capturedDestination)}
      />
    );
  }

  return (
    /*
     * Ability rules are published per group rather than in the root layout; the
     * open layout mounts its own provider for the community and content-library
     * screens a signed-in reader can browse there. `useAbility` degrades to
     * deny-all rather than throwing, so the guards above returning null costs at
     * most a hidden control.
     */
    <AppAbilityProvider userId={userId}>
      {isWeb ? <SecureNavBar pathname={pathname} /> : null}

      <ActingAsBanner />

      <GlobalBannerSlot />

      <Stack screenOptions={{ ...BASE_STACK_SCREEN_OPTIONS, statusBarStyle }}>
        {/*
         * Keep this first. Declared screens are ordered ahead of undeclared ones, in the
         * order written here, and the first screen is the navigator's initial route because
         * nothing sets `initialRouteName`. Moving it down silently relocates where the
         * secure group opens.
         */}
        <Stack.Screen
          name="(tabs)"
          options={{
            ...BASE_STACK_SCREEN_OPTIONS,
            statusBarBackgroundColor: colors.background[0],
            statusBarTranslucent: true,
          }}
        />

        {/*
         * Routes come from the filesystem whether or not this layout names them, so a
         * declaration changes nothing by itself. It is what a guard attaches to: a guard can
         * only drop a name the navigator already knows, so a screen missing from this list is
         * a screen no rule here can reach, and nothing fails to say so.
         *
         * Every screen below that a reader awaiting NPI verification is held out of is
         * wrapped **where it already sits**, rather than gathered into one block. Grouping is
         * not what makes a guard work — being a literal direct child of this `Stack` is — and
         * moving a declaration moves its position in the navigator, which the comment above
         * shows is load-bearing for at least one of them.
         */}
        <Stack.Protected guard={mayUseVerifiedRoutes}>
          <Stack.Screen
            name="feedback/[id]"
            options={{
              ...BASE_STACK_SCREEN_OPTIONS,
              headerShown: true,
              presentation: 'modal',
              statusBarBackgroundColor: colors.background[0],
              statusBarTranslucent: true,
            }}
          />

          <Stack.Screen
            name="feedback/success"
            options={{
              ...BASE_STACK_SCREEN_OPTIONS,
              headerShown: true,
              presentation: 'modal',
              statusBarBackgroundColor: colors.primary[500],
              statusBarStyle: 'light',
              statusBarTranslucent: true,
              contentStyle: {
                backgroundColor: colors.primary[500],
              },
            }}
          />
        </Stack.Protected>

        <Stack.Protected guard={mayUseVerifiedRoutes}>
          <Stack.Screen
            name="inbox/chat/citation-wizard"
            options={{
              ...BASE_STACK_SCREEN_OPTIONS,
              headerShown: true,
              presentation: 'fullScreenModal',
              gestureEnabled: false,
              animation: 'slide_from_bottom',
              statusBarBackgroundColor: colors.background[0],
              statusBarTranslucent: true,
            }}
          />
          <Stack.Screen
            name="inbox/chat/reply-draft"
            options={{
              ...BASE_STACK_SCREEN_OPTIONS,
              headerShown: true,
              presentation: 'fullScreenModal',
              gestureEnabled: false,
              animation: 'slide_from_bottom',
              statusBarBackgroundColor: colors.background[0],
              statusBarTranslucent: true,
            }}
          />

          <Stack.Screen
            name="survey/[id]"
            options={{
              ...BASE_STACK_SCREEN_OPTIONS,
              headerShown: true,
              presentation: 'fullScreenModal',
              gestureEnabled: false,
              animation: 'slide_from_bottom',
              statusBarBackgroundColor: colors.background[0],
              statusBarTranslucent: true,
            }}
          />
        </Stack.Protected>

        {/*
         * The two screens a reader is *parked* on until they finish something. Both stay
         * unguarded, and the reason is not that no rule applies — it is that the obvious rule
         * is a trap. A guard keyed on the condition that parked them here would close the
         * instant that condition clears, while the reader is still standing on the screen,
         * and the router's fallback would then race the screen's own `navigateToRedirectPath`
         * and drop the destination the detour was carrying. The redirect chain above decides
         * these two alone.
         */}
        <Stack.Screen name="confirm-account-details" />

        <Stack.Screen name="npi-verification" />

        {/*
         * Staff tooling. Falling back to `(tabs)` is a change from the `/account` this screen
         * used to redirect to — worth knowing, not worth machinery to preserve on a debug
         * surface.
         */}
        <Stack.Protected guard={mayUsePrimumSettings}>
          <Stack.Screen name="primum-settings" />
        </Stack.Protected>

        <Stack.Protected guard={mayUseVerifiedRoutes}>
          <Stack.Screen name="update-password" />
        </Stack.Protected>

        <Stack.Screen name="inquiry/redirect" />

        <Stack.Screen name="content-library/index" />

        <Stack.Protected guard={mayUseVerifiedRoutes}>
          <Stack.Screen name="account/index" />

          <Stack.Screen name="account/contact-details/index" />

          <Stack.Screen name="account/contact-details/edit" />

          <Stack.Screen name="account/delete-account" />

          <Stack.Screen name="account/invites" />

          <Stack.Screen name="account/notification-settings" />

          <Stack.Screen name="account/polls/index" />

          <Stack.Screen name="account/polls/[groupId]" />

          <Stack.Screen name="account/qr-code" />

          <Stack.Screen name="profile/sms-notification-modal" />
        </Stack.Protected>
      </Stack>
    </AppAbilityProvider>
  );
}
